import { useMemo } from 'react'
import { Table, StatusHint } from '@indipia/ui-core'
import type { OrderVendorRow } from '../../api/orderApi'
import type { GanttDeepLink, ScheduleDetailTask } from '../../types/gantt.types'
import { LIST_EMPTY_TEXT, type ListDisplayItem, type NextAction, type ScheduleListRow } from '../../types/scheduleList.types'
import { buildScheduleListColumns } from './ScheduleListRow'
import { ScheduleListExpand } from './ScheduleListExpand'
import { ScheduleListCustReqLine, hasCustReqLine } from './ScheduleListCustReqLine'

// 표(D-04) — ui-core Table 의 펼침 모드(승인 N-02)로 머리글·공종 행·마일스톤 행·펼침 영역을 그린다.
// 행 클릭 = 해당 공종 펼침 토글(여러 행 동시 펼침, taskId 기준). 행 안 버튼·펼침 안 버튼 클릭은 Table 이 토글로 취급하지 않는다.
// 필터 통과 0건이면 표 안에 한 줄 '조건에 맞는 공종이 없어요'. 머리글은 스크롤 영역 위쪽에 붙는다(stickyHeader).

/** 최소 폭 — 열이 찌그러지지 않게 스크롤 영역(PageScroll)이 가로로 스크롤된다 */
const TABLE_MIN_WIDTH = 'calc(var(--num-160) * 6)'

export interface ScheduleListTableProps {
  /** 정렬·필터·마일스톤 병합이 끝난 표시 목록(mergeMilestones 결과) */
  items: readonly ListDisplayItem[]
  /** 펼쳐진 공종 taskId 집합(useScheduleListState.expanded) */
  expandedIds: ReadonlySet<number>
  onToggleExpanded: (taskId: number) => void
  /** 선행공종 줄이 작업 상태를 찾는 데 쓰는 현장 전체 행(필터 전) */
  allRows: readonly ScheduleListRow[]
  /** 공종 상세 응답의 tasks */
  tasks: readonly ScheduleDetailTask[]
  /** 발주 원장 행(발주 조회 실패 시 빈 배열) */
  vendorRows: readonly OrderVendorRow[]
  today: string
  canRunNextAction: (action: NextAction | null) => boolean
  onRunNextAction: (taskId: number, action: NextAction) => void
  onOpenDetail: (taskId: number, card?: GanttDeepLink['card']) => void
  canOpenDetail: boolean
  /** 머리글이 붙는 위치(CSS 길이). 상단에 고정 영역이 있으면 그 높이 */
  stickyTop?: string
}

export function ScheduleListTable({
  items, expandedIds, onToggleExpanded, allRows, tasks, vendorRows, today,
  canRunNextAction, onRunNextAction, onOpenDetail, canOpenDetail, stickyTop,
}: ScheduleListTableProps) {
  const columns = useMemo(
    () => buildScheduleListColumns({ canRunNextAction, onRunNextAction }),
    [canRunNextAction, onRunNextAction],
  )
  const rowsById = useMemo(() => new Map(allRows.map(r => [r.taskId, r] as const)), [allRows])
  const taskById = useMemo(() => new Map(tasks.map(t => [t.taskId, t] as const)), [tasks])

  return (
    <Table<ListDisplayItem>
      columns={columns}
      rows={items}
      rowKey={it => (it.kind === 'task' ? it.row.taskId : `m-${it.milestone.milestoneId}`)}
      expandedKeys={expandedIds}
      // 스케줄 전용 행정 공정은 시공·자재가 없어 펼침 상세(진행 단계·간트 상세창 이동)가 없다
      onRowClick={it => { if (it.kind === 'task' && !it.row.scheduleOnly) onToggleExpanded(it.row.taskId) }}
      renderExpanded={it => {
        if (it.kind !== 'task' || it.row.scheduleOnly) return null
        const task = taskById.get(it.row.taskId)
        if (!task) return <StatusHint tone="muted">공종 정보를 찾을 수 없어요</StatusHint>
        return (
          <ScheduleListExpand
            row={it.row}
            task={task}
            vendorRows={vendorRows}
            rowsById={rowsById}
            today={today}
            onOpenDetail={onOpenDetail}
            canOpenDetail={canOpenDetail}
            customerRequest={hasCustReqLine(task)
              ? <ScheduleListCustReqLine counts={task} onOpenDetail={canOpenDetail ? () => onOpenDetail(task.taskId, 'cr') : undefined} />
              : undefined}
          />
        )
      }}
      empty={LIST_EMPTY_TEXT}
      stickyHeader
      stickyTop={stickyTop}
      style={{ minWidth: TABLE_MIN_WIDTH }}
    />
  )
}
